import { Dispatch, SetStateAction, useState } from 'react';
import { Device } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { UseMutationResult } from 'react-query';
import { createContext, useContextSelector } from 'use-context-selector';
import { OutboundSelectedPhoneNumberProps } from '@frontend/generic-dialpad-accessories';
import { getCurrentUserSoftphoneDisconnectWarningStorageKey } from '../utils/storage-keys';
import { useSoftphoneClient } from './softphone-client-provider';
import { useSoftphoneEventsEmitter } from './softphone-events-provider';

type SoftphoneSettingsContextValue = {
  showSettings: () => void;
  hideSettings: () => void;
  isShowingSettings: boolean;
  isDoNotDisturb: boolean;
  enableDoNotDisturb: () => void;
  disableDoNotDisturb: () => void;
  turnOffSoftphone: () => void;
  availablePhoneNumbers: OutboundSelectedPhoneNumberProps[];
  setCurrentPhoneNumber: Dispatch<SetStateAction<OutboundSelectedPhoneNumberProps | undefined>>;
  selectedOutboundPhoneNumber: OutboundSelectedPhoneNumberProps | undefined;
  phoneConfigTenantId: string | undefined;
  isCallWaitingIndicatorBeepEnabled: boolean;
  device: Device | undefined;
};

const SoftphoneSettingsContext = createContext({} as SoftphoneSettingsContextValue);

type Props = {
  children: React.ReactNode;
  setCurrentPhoneNumber: Dispatch<SetStateAction<OutboundSelectedPhoneNumberProps | undefined>>;
  availablePhoneNumbers: OutboundSelectedPhoneNumberProps[];
  selectedOutboundPhoneNumber: OutboundSelectedPhoneNumberProps | undefined;
  phoneConfigTenantId: string | undefined;
  isCallWaitingIndicatorBeepEnabled: boolean;
  openDisconnectModal: () => void;
  disconnectDevice: () => void;
  updateDnd: UseMutationResult<{ doNotDisturb: boolean }, unknown, { enabled: boolean }, unknown>;
  isDndEnabled: boolean;
  device: Device | undefined;
};

export const SoftphoneSettingsProvider = ({
  availablePhoneNumbers,
  setCurrentPhoneNumber,
  selectedOutboundPhoneNumber,
  phoneConfigTenantId,
  isCallWaitingIndicatorBeepEnabled,
  children,
  openDisconnectModal,
  disconnectDevice,
  updateDnd,
  isDndEnabled,
  device,
}: Props) => {
  const [isShowingSettings, setIsShowingSettings] = useState(false);
  const [isDoNotDisturb, setIsDoNotDisturb] = useState(isDndEnabled);
  const emitter = useSoftphoneEventsEmitter();
  const disconnect = useSoftphoneClient((ctx) => ctx.disconnect);
  const disconnectKey = getCurrentUserSoftphoneDisconnectWarningStorageKey();

  const showSettings = () => {
    setIsShowingSettings(true);
  };

  const hideSettings = () => {
    setIsShowingSettings(false);
  };

  const enableDoNotDisturb = () => {
    emitter.emit('do-not-disturb.enabled', null);
    updateDnd.mutate({ enabled: true });
    setIsDoNotDisturb(true);
  };

  const disableDoNotDisturb = () => {
    emitter.emit('do-not-disturb.disabled', null);
    updateDnd.mutate({ enabled: false });
    setIsDoNotDisturb(false);
  };

  const turnOffSoftphone = () => {
    if (localStorage.getItem(disconnectKey) === 'true') {
      disconnect();
      disconnectDevice();
    } else {
      openDisconnectModal();
    }
  };

  const value = {
    showSettings,
    hideSettings,
    isShowingSettings,
    turnOffSoftphone,
    isDoNotDisturb,
    enableDoNotDisturb,
    disableDoNotDisturb,
    availablePhoneNumbers,
    setCurrentPhoneNumber,
    selectedOutboundPhoneNumber,
    phoneConfigTenantId,
    isCallWaitingIndicatorBeepEnabled,
    device,
  } satisfies SoftphoneSettingsContextValue;

  return <SoftphoneSettingsContext.Provider value={value}>{children}</SoftphoneSettingsContext.Provider>;
};

export const useSoftphoneSettings = <T extends any>(selector: (value: SoftphoneSettingsContextValue) => T) => {
  return useContextSelector(SoftphoneSettingsContext, selector);
};
